import {store} from '../../../redux/store';
import {login, setConfig, setUniqueId} from './reducer';
import {
  finishLoading,
  startLoading,
} from '../../../connected-components/loading/actions';
import {tr} from '../../../translations';
import {showGlobalToast} from '../../../connected-components/toast/actions';
import {setLocalData, getLocalData} from '../../../utils/storage';
import {services} from '../../../api';
import {LoginForm} from '../../../interfaces/api/auth/login';
import {keys} from '../../../api/keys';
import {
  CurrentUserConfig,
  CurrentUserType,
} from '../../../interfaces/reducers/auth';
import Config from 'react-native-config';
import {getHeaders} from '../../../utils/headers';
import {ToastType} from '../../../utils/enums';
import {RegisterForm} from '../../../interfaces/api/auth/register';
import {isWeb} from '../../../utils/responsive';
import {navigationRef} from '../../../navigation';
import screens from '../../../config/screens';

const replaceUrlParams = (paramsToRemove: string[]) => {
  if (!isWeb) return;
  const url = new URL(window.location.href);
  paramsToRemove.forEach(p => url.searchParams.delete(p));
  // Ensure we keep unid intact
  const unid = url.searchParams.get('unid');
  const path =
    url.pathname +
    (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '');
  window.history.replaceState({}, '', path);
  // Re-assert unid in Redux if changed
  if (unid !== store.getState().auth.uniqueId) {
    store.dispatch(setUniqueId(unid));
  }
};

/**
 * this function to log in
 */
export const logIn = async (body: LoginForm): Promise<boolean> => {
  try {
    startLoading();
    const headers = await getHeaders();
    const res = await services.auth.login(body, headers);
    const user: CurrentUserType = {
      ...(res?.content?.user || {}),
      token: res?.content?.token || '',
      password: body?.password,
    };
    const {config} = store.getState().auth || {};
    if (Object.keys(config || {}).length < 2) {
      store.dispatch(setConfig(res?.content?.config));
    }

    await startSession({
      ...(res?.content?.user || {}),
      profession_id: 1,
      token: res?.content?.token,
      password: body?.password,
    });

    showGlobalToast({
      message: tr('login.loginSuccessMessage') + user?.first_name,
      type: ToastType.SUCCESS,
    });
    return true;
  } catch (error: any) {
    
    // Check if error is due to unverified email (error code 1001)
    const errorCode = error?.response?.data?.error_code || error?.data?.error_code;
    if (errorCode === 1001) {
      // Navigate to verification pending screen with email
      navigationRef.current?.navigate(screens.auth.verificationPending, {
        email: body.login,
        message: tr('login.emailNotVerified'),
      });
    }
    
    return false;
  } finally {
    finishLoading();
  }
};

export const startSession = async (user: CurrentUserType) => {
  try {
    let currentUser = {...user};
    if (
      (currentUser?.email || '').trim().toLowerCase() === Config.APPLE_EMAIL
    ) {
      currentUser = {
        ...user,
        specialty_id: 1,
        real_specialty_id: user?.specialty_id,
      };
    }

    if (isWeb) {
      const searchParams = new URLSearchParams(window.location.search);
      const urlUnid = searchParams.get('unid');
      const urlId = searchParams.get('id');
      const urlToken = searchParams.get('token');

      let users = getLocalData(keys.CURRENT_USER) || {};

      // If id/token in URL, use id as unid and store ehr_token temporarily
      const effectiveUnid = (urlId || urlUnid || 'default') as string;
      const ehrToken = urlToken || null;

      // Merge user with existing stored one if exists
      const existing = users[effectiveUnid] || {};
      const mergedUser: CurrentUserType = {
        ...existing,
        ...currentUser,
        ehr_token: ehrToken ?? existing?.ehr_token ?? null,
      };

      users[effectiveUnid] = mergedUser;
      setLocalData(keys.CURRENT_USER, users);

      // Set UNID in Redux
      store.dispatch(
        setUniqueId(effectiveUnid === 'default' ? null : effectiveUnid),
      );

      // Clean id & token from URL (keep unid)
      replaceUrlParams(['id', 'token']);

      // Update Redux user to include ehr_token
      store.dispatch(login(mergedUser));
      return true;
    } else {
      // Traditional storage for mobile
      setLocalData(keys.CURRENT_USER, currentUser);
      store.dispatch(login(currentUser));
      return true;
    }
  } catch (error) {
    return false;
  }
};

/**
 * this function to register
 */
export const register = async (body: RegisterForm): Promise<boolean> => {
  try {
    startLoading();
    const headers = await getHeaders();
    const res = await services.auth.register(body, headers);
    
    // Check if email verification is required
    if (res?.content?.requires_verification) {
      // Navigate to verification pending screen with email
      navigationRef.current?.navigate(screens.auth.verificationPending, {
        email: res?.content?.user?.email || body.email,
        message: res?.content?.message || tr('register.verificationRequired'),
      });
      showGlobalToast({
        message: res?.content?.message || tr('register.verificationRequired'),
        type: ToastType.SUCCESS,
      });
      return true;
    }
    
    // If no verification required, proceed with normal login flow
    await startSession({
      ...(res?.content?.user || {}),
      token: res?.content?.token,
      password: body?.password,
    });
    showGlobalToast({
      message:
        tr('login.registerSuccessMessage') + res?.content?.user.first_name,
      type: ToastType.SUCCESS,
    });
    return true;
  } catch (error: any) {
    return false;
  } finally {
    finishLoading();
  }
};

/**
 * clear user session.
 */
export const endSession = async () => {
  try {
    startLoading();
    const {currentUser, config: userConfig} = store.getState().auth || {};

    if (isWeb) {
      const users = getLocalData(keys.CURRENT_USER);
      if (users) {
        const searchParams = new URLSearchParams(window.location.search);
        const unid = searchParams.get('unid');

        const updatedUsers = {...users};
        if (unid) {
          delete updatedUsers[unid];
        } else {
          delete updatedUsers.default;
        }

        setLocalData(keys.CURRENT_USER, updatedUsers);
      }
    }

    await startSession({
      ...currentUser,
      ...userConfig,
      token: null,
      is_first_login: 0,
    });

    showGlobalToast({
      message: tr('login.logoutSuccessfully'),
      type: ToastType.SUCCESS,
    });
    return true;
  } catch (error) {
    return false;
  } finally {
    finishLoading();
  }
};

/**
 * Updates the current configuration asynchronously.
 */
export const updateConfig = async (): Promise<boolean> => {
  try {
    const res = await services.auth.getConfig();
    const {font} = store.getState().auth.config || {};
    if (res?.content?.data) {
      const tempConfig: CurrentUserConfig = {
        ...(res?.content?.data || {}),
        font,
      };
      store.dispatch(setConfig(tempConfig));
    }
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Resend verification email
 */
export const resendVerification = async (email: string): Promise<boolean> => {
  try {
    startLoading();
    const res = await services.auth.resendVerification(email);
    showGlobalToast({
      message: res?.content?.message || tr('register.verificationEmailSent'),
      type: ToastType.SUCCESS,
    });
    return true;
  } catch (error: any) {
    return false;
  } finally {
    finishLoading();
  }
};
